import axios, { AxiosRequestConfig, AxiosResponse, Method } from "axios";
import { renewAccessToken, deleteSession } from "store/actions/session";
import { errorAlert } from "utils/alerts";
import store from "store";

const API_URL: string | undefined = process.env.REACT_APP_API_URL;
const MANAGER_PORTAL_URL: string | undefined = process.env.REACT_APP_MANAGER_PORTAL_URL;
const NODE_ENV: string | undefined = process.env.NODE_ENV;

let isAlreadyFetchingAccessToken: boolean = false;
let subscribers: Array<(accessToken: string) => void> = [];

type RequestOptions = {
  params?: unknown;
  data?: unknown;
  [key: string]: unknown;
};

type AxiosCustomRequestConfig = AxiosRequestConfig & {
  authenticated?: boolean;
};

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    "content-type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config: AxiosCustomRequestConfig) => {
    const token: string | null = localStorage.getItem("accessToken");

    if (config.authenticated && token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const originalRequest = {...error.config};

    if (error.response.status === 401 && originalRequest._retry !== true && originalRequest.authenticated) {
      if (!localStorage.getItem("refreshToken")) {
        redirectToAuth(document.location.pathname);

        return Promise.reject(error);
      }

      originalRequest._retry = true;

      const retryOriginalRequest = new Promise(resolve => {
        addSubscriber((accessToken: string) => {
          originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
          error.response.config.headers["Authorization"] = `Bearer ${accessToken}`;

          resolve(axiosInstance.request(originalRequest));
        })
      });

      if (!isAlreadyFetchingAccessToken) {
        isAlreadyFetchingAccessToken = true;

        return store
          .dispatch(renewAccessToken())
          .then(() => {
            const token: string = localStorage.getItem("accessToken") as string;
            isAlreadyFetchingAccessToken = false;
            onAccessTokenFetched(token);

            return retryOriginalRequest;
          })
          .catch(() => {
            store.dispatch(deleteSession());
            isAlreadyFetchingAccessToken = false;
          });
      }
    }

    return Promise.reject(error);
  }
);

export async function apiRequest(
  method: Method,
  path: string,
  options: RequestOptions = {},
  authenticated: boolean = true
): Promise<AxiosResponse> {
  try {
    return await axiosInstance.request({
      method: method,
      url: `/${path}`,
      authenticated,
      responseType: options.responseType,
      params: options.params,
      data: JSON.stringify(options.data) || {},
    } as AxiosCustomRequestConfig);
  } catch (error) {
    if (error.response && error.response.status !== 400) {
      if (
        typeof error.response.data.error === "string" &&
        error.response.data.error
      ) {
        if (error.response.data.error !== "Wrong number of segments") {
          errorAlert(error.response.data.error);
        }
      }

      if (
        typeof error.response.data.error === "object" &&
        error.response.data.error
      ) {
        errorAlert(error.response.data.error.message);
      }
    }
    
    return Promise.reject(error.response);
  }
};

export const redirectToAuth = (path: string): void => {
  document.location.href = NODE_ENV === 'development' ? 
    '/invalid-token': 
    `${MANAGER_PORTAL_URL}login.php?redirect-to-rdo=${path}`;
};

const onAccessTokenFetched = (accessToken: string) => {
  subscribers.forEach(callback => callback(accessToken));
  subscribers = [];
};

function addSubscriber(callback: (accessToken: string) => void) {
  subscribers.push(callback);
};